import {
  PopConfirm,
  Button,
  EllipsisSpan,
  Table,
  Wrapper,
  useSearchParams,
  useUpdate,
  useBreadcrumbRoutes,
  Paging,
} from '@maxtropy/components';
import { useRequest } from 'ahooks';
import { Space } from 'antd';
import { PlusOutlined } from '@ant-design/icons';
import {
  apiV2PeriodDeletePost,
  apiV2PeriodPagePost,
  V2PeriodPagePostResponse,
} from '@maxtropy/device-customer-apis-v2';
import { ColumnsType } from 'antd/es/table';
import { useNavigate } from 'react-router';
import { isNil } from 'lodash';
import dayjs from 'dayjs';
import { useState } from 'react';
import AddPeriodModal from './components/AddPeriodModal';
import CopyPeriodModal from './components/CopyPeriodModal';

export type PeriodItem = Exclude<V2PeriodPagePostResponse['list'], undefined>[number];

const columns: ColumnsType<PeriodItem> = [
  {
    title: '数据查询时段',
    dataIndex: 'name',
    ellipsis: { showTitle: true },
    render: (v: string) => <EllipsisSpan value={v} />,
  },
  {
    title: '最后操作人',
    dataIndex: 'lastUpdateUserName',
    ellipsis: { showTitle: true },
    render: (v: string) => <EllipsisSpan value={v} />,
  },
  {
    title: '最后操作时间',
    dataIndex: 'updateTime',
    ellipsis: { showTitle: true },
    render: (v: string) => <EllipsisSpan value={dayjs(v).format('YYYY-MM-DD HH:mm:ss')} />,
  },
];

const DataQueryPeriod: React.FC = () => {
  const navigate = useNavigate();
  const pagingInfo = useSearchParams(20);
  const { pageOffset, pageSize, setTotalCount, setPageOffset } = pagingInfo;
  const [update, updateFn] = useUpdate();
  const breadcrumbRoutes = useBreadcrumbRoutes();

  const [currentPeriod, setCurrentPeriod] = useState<PeriodItem>();
  const [addModal, setAddModal] = useState<boolean>(false);
  const [copyModal, setCopyModal] = useState<boolean>(false);

  const { data, loading } = useRequest(
    () =>
      apiV2PeriodPagePost({
        page: pageOffset,
        size: pageSize,
      }).then(res => {
        setTotalCount(res.total ?? 0);
        return res.list ?? [];
      }),
    {
      refreshDeps: [pageOffset, pageSize, update],
    }
  );

  const onDelete = (id: number) => {
    if (isNil(id)) return;
    apiV2PeriodDeletePost({ id }).then(res => {
      updateFn();
    });
  };

  const buildColumns = [
    ...columns,
    {
      title: '操作',
      dataIndex: 'operation',
      width: 240,
      fixed: 'right' as 'right',
      render: (value: undefined, record: any) => {
        return (
          <Space size={16}>
            <Button
              type="link"
              onClick={() => {
                setCurrentPeriod(record);
                setCopyModal(true);
              }}
            >
              复制
            </Button>
            <Button
              type="link"
              onClick={() => {
                navigate(`/data/query/period/config/${record.id}`);
              }}
            >
              配置时间段
            </Button>
            <Button
              type="link"
              onClick={() => {
                setCurrentPeriod(record);
                setAddModal(true);
              }}
            >
              编辑
            </Button>
            <PopConfirm placement="top" title="是否确认删除此时段" onConfirm={() => onDelete(record.id)}>
              <Button type="link">删除</Button>
            </PopConfirm>
          </Space>
        );
      },
    },
  ];

  return (
    <Wrapper routes={[...(breadcrumbRoutes?.routes ?? [])]}>
      <Space style={{ marginBottom: 10 }}>
        <Button
          type="primary"
          icon={<PlusOutlined />}
          onClick={() => {
            setCurrentPeriod(undefined);
            setAddModal(true);
          }}
        >
          新增数据查询时段
        </Button>
      </Space>

      <Table loading={loading} rowKey="id" columns={buildColumns} dataSource={data} />
      <Paging pagingInfo={pagingInfo} />
      {addModal && (
        <AddPeriodModal
          period={currentPeriod}
          modalVisible={addModal}
          setModalVisible={setAddModal}
          forceUpdate={updateFn}
        />
      )}
      {copyModal && (
        <CopyPeriodModal
          period={currentPeriod!}
          modalVisible={copyModal}
          setModalVisible={setCopyModal}
          forceUpdate={updateFn}
        />
      )}
    </Wrapper>
  );
};

export default DataQueryPeriod;
